import React, { ComponentProps, FC, ReactNode, useCallback } from 'react';
import { ProjectId } from 'api/models';
import { ActionPanel } from 'components/molecules/action-panel';
import { BulkAssigneePicker } from 'components/molecules/assignee-picker';
import {
  AssigneeField,
  AssigneeValue,
} from 'components/organisms/tasks/fields';
import { useTranslation } from 'hooks/use-translation';
import { CreatableEditableRefreshableContent, Empty } from 'stores/core';
import { Task } from 'stores/tasks';
import { OnUpdate } from '../bulk-editor';
import { getTransformer } from './get-transformer';

const { PopoverAction } = ActionPanel;

type Props = {
  assignee: AssigneeField;
  ids: string[];
  lookup: Record<number, CreatableEditableRefreshableContent<Task>>;
  title: ReactNode;
  allowed: ComponentProps<typeof PopoverAction>['allowed'];
  onUpdate: OnUpdate;
  projectIds: ProjectId[];
  isPrivate: boolean;
};

export const AddAssignee: FC<Props> = ({
  assignee,
  ids,
  lookup,
  projectIds,
  isPrivate,
  title,
  allowed,
  onUpdate,
}) => {
  const onAssigneeChange = useCallback(
    (value: AssigneeValue[]) => {
      const transformer = getTransformer(value)(assignee);

      onUpdate(
        ids
          .map((id) => lookup[id as unknown as number] ?? Empty())
          .map(([, x]) => x as Task)
          .filter(Boolean)
          .map(transformer),
      );
    },
    [onUpdate, ids, lookup, assignee],
  );
  const saveTitle = useTranslation(
    'task_list.bulk_editor.options.add_assignee.save',
  );

  return (
    <BulkAssigneePicker
      title={title}
      allowed={allowed}
      saveTitle={saveTitle}
      assignee={assignee}
      isPrivate={isPrivate}
      projectIds={projectIds}
      onAssigneeChange={onAssigneeChange}
    />
  );
};
